import { h } from 'preact';
import { Text } from 'preact-i18n';

import Avatar from '../gui/avatar';

// Stevne RSVP («Skal / Interessert / Kanskje») shown Facebook-style: stacked avatars of
// the club members who responded, three toggle buttons for the viewer, and the grouped
// name list in the detail view. Pure props → markup; the store lives in the route.
export const STATUS_ORDER = ['going', 'interested', 'maybe'];

const STATUS_META = {
    going: {
        id: 'terminliste.rsvp.going', text: 'Going', icon: 'fa-solid fa-check', active: 'btn-success', mark: 'fa-solid fa-star text-warning', title: 'Skal',
    },
    interested: {
        id: 'terminliste.rsvp.interested', text: 'Interested', icon: 'fa-solid fa-heart', active: 'btn-danger', mark: 'fa-solid fa-heart text-danger', title: 'Interessert',
    },
    maybe: {
        id: 'terminliste.rsvp.maybe', text: 'Maybe', icon: 'fa-solid fa-question', active: 'btn-info', mark: 'fa-solid fa-circle-question text-info', title: 'Kanskje',
    },
};

const EMPTY = { counts: { going: 0, interested: 0, maybe: 0 }, people: [], mine: '' };

function sortedPeople(summary) {
    const people = Array.isArray(summary.people) ? summary.people : [];
    return [...people].sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
}

function totalOf(summary) {
    const counts = summary.counts || {};
    return STATUS_ORDER.reduce((sum, key) => sum + (counts[key] || 0), 0);
}

/** My own status as one small icon at the start of a list line: star / heart / question mark. */
export function RsvpMark({ mine = '' }) {
    const meta = STATUS_META[mine];
    if (!meta) {
        return null;
    }
    return (
        <i
            class={`${meta.mark} nsf-rsvp-mark`}
            data-status={mine}
            title={meta.text}
            aria-label={meta.text}
        />
    );
}

/** Stacked round avatars, going first, «+N» for the rest. Nothing without responses. */
export function RsvpAvatars({
    summary = EMPTY, max = 4, size = 22, onOpenProfile,
}) {
    const total = totalOf(summary);
    if (total === 0) {
        return null;
    }
    const people = sortedPeople(summary);
    const shown = people.slice(0, max);
    const rest = total - shown.length;
    const title = people.map(p => p.name).filter(Boolean).join(', ');
    return (
        <span class='d-inline-flex align-items-center align-middle ms-1 nsf-rsvp-avatars' title={title}>
            {shown.map((person, idx) => (
                <span
                    key={person.id}
                    class={`d-inline-block rounded-circle border border-body ${idx > 0 ? 'ms-n1' : ''}`}
                    style={`margin-left: ${idx > 0 ? '-6px' : '0'}; cursor: ${onOpenProfile ? 'pointer' : 'default'};`}
                    data-user-id={person.id}
                    data-status={person.status}
                    onClick={onOpenProfile ? (e) => { e.preventDefault(); e.stopPropagation(); onOpenProfile(person); } : undefined}
                >
                    <Avatar name={person.name} image={person.image} faceBox={person.faceBox} size={size} />
                </span>
            ))}
            {rest > 0 && <span class='small text-body-secondary ms-1'>+{rest}</span>}
        </span>
    );
}

/** The viewer's three toggles with counts. Hidden when logged out. */
export function RsvpButtons({
    summary = EMPTY, loggedIn = false, onSet, size = 'sm',
}) {
    if (!loggedIn) {
        return null;
    }
    const counts = summary.counts || {};
    return (
        <div class={`btn-group btn-group-${size} nsf-rsvp-buttons`} role='group'>
            {STATUS_ORDER.map((status) => {
                const meta = STATUS_META[status];
                const active = summary.mine === status;
                return (
                    <button
                        key={status}
                        type='button'
                        class={`btn text-nowrap ${active ? meta.active : 'btn-outline-secondary'}`}
                        data-status={status}
                        aria-pressed={active}
                        onClick={onSet ? (e) => { e.preventDefault(); e.stopPropagation(); onSet(status); } : undefined}
                    >
                        <i class={`${meta.icon} me-1`} />
                        <Text id={meta.id}>{meta.text}</Text>
                        {counts[status] > 0 && <span class='ms-1'>({counts[status]})</span>}
                    </button>
                );
            })}
        </div>
    );
}

/** Names grouped under each status (detail view); empty groups are skipped. */
export function RsvpPeople({ summary = EMPTY, onOpenProfile }) {
    const people = sortedPeople(summary);
    if (people.length === 0) {
        return null;
    }
    return (
        <div class='nsf-rsvp-people'>
            {STATUS_ORDER.map((status) => {
                const group = people.filter(p => p.status === status);
                if (group.length === 0) {
                    return null;
                }
                const meta = STATUS_META[status];
                return (
                    <div key={status} class='mb-2'>
                        <div class='small fw-semibold'>
                            <i class={`${meta.icon} me-1`} />
                            <Text id={meta.id}>{meta.text}</Text> ({group.length})
                        </div>
                        <div class='d-flex flex-wrap gap-2 mt-1'>
                            {group.map(person => (
                                <span
                                    key={person.id}
                                    class='d-inline-flex align-items-center gap-1 small'
                                    style={onOpenProfile ? 'cursor: pointer;' : ''}
                                    data-user-id={person.id}
                                    onClick={onOpenProfile ? () => onOpenProfile(person) : undefined}
                                >
                                    <Avatar name={person.name} image={person.image} faceBox={person.faceBox} size={24} />
                                    {person.name}
                                </span>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default RsvpAvatars;
